import { memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useViewport } from '@chilecompra/react-kit'
import { Button, Card, Toast, Typography } from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'

import { USER_TYPES } from '../../configs/settings/constants'
import { useAuthProvider } from '../AuthProvider/AuthProvider.hook'
import { onResetResponseError } from './RequestDetailInfo.actions'
import QuestionsList from './RequestDetailInfo.questionsList'
import {
  ArrowDownIcon,
  Container,
  DownloadLink,
  Item,
  SearchLoadingIcon,
  Skeleton,
  Strong,
  Tab,
  Tabs,
  UniquePassIcon,
  UniquePassLink
} from './RequestDetailInfo.styles'

/**
 * The RequestDetailInfo's questionsAndAnswers.
 */
const QuestionsAndAnswers = props => {
  const {
    countries,
    detailEntCode,
    detailOrgCode,
    detailUsrCode,
    errorCountries,
    isClose,
    isDownloading,
    isError,
    isErrorDownloading,
    isLoading,
    isWillBePublic,
    loadingCountries,
    onAddFile,
    onDownloadFile,
    onDownloadQuestionsAndAnswers,
    onRemoveFile,
    onResponse,
    onSignedUniquePass,
    publicUserDni,
    publicUserName,
    questions
  } = props

  const dispatch = useDispatch()
  const { size } = useViewport()
  const { isPrivate, userType } = useAuthProvider()
  const {
    authInfo: { entCode, orgCode, usrCode }
  } = useSelector(state => state.authProvider)

  const changeTabs = () => {
    const someQuestionError = questions.some(question => question.error)

    if (!someQuestionError) return

    dispatch(onResetResponseError())
  }

  return (
    <Item margin="32px 0 16px" md={9} order={4} xs={12}>
      <Container>
        <Typography fontWeight="bold" margin="0 0 32px" variant={size.isDownToTiny ? 'h3' : 'h2'}>
          Preguntas y respuestas
        </Typography>
      </Container>

      {isLoading && (
        <>
          <Skeleton className="questions__and__answers" margin="0 0 16px" width="100%" />
          <Skeleton className="questions__and__answers" margin="0 0 16px" width="100%" />
          <Skeleton className="questions__and__answers" width="100%" />
        </>
      )}

      {!isError && !isLoading && (
        <>
          {questions.length > 0 && (
            <>
              {isWillBePublic && (
                <Toast margin="0 0 32px" severity="info">
                  Las respuestas que se ingresen en esta sección serán de caracter <Strong>público</Strong>, y todos los
                  usuarios podrán visualizarlas.
                </Toast>
              )}

              {!isWillBePublic && (
                <Toast margin="0 0 32px" severity="info">
                  Las respuestas que {userType === USER_TYPES.BUYER ? 'ingresen los usuarios' : 'ingreses'} en esta
                  sección serán de caracter <Strong>privado</Strong>, y solamente{' '}
                  {userType === USER_TYPES.BUYER ? 'tú podrás' : 'el organismo comprador podrá'} visualizarlas.
                </Toast>
              )}

              {isWillBePublic && (
                <Container justifyContent="flex-end" margin="0 0 16px">
                  <DownloadLink
                    disabled={isDownloading}
                    isErrorDownloading={!!isErrorDownloading}
                    onClick={onDownloadQuestionsAndAnswers}
                  >
                    {isDownloading && 'Descargando...'}
                    {!isDownloading && isErrorDownloading && 'Reintente la descarga de nuevo...'}
                    {!isDownloading && !isErrorDownloading && 'Descargar resultados en excel'}
                  </DownloadLink>

                  {isDownloading && <SearchLoadingIcon />}

                  {!isDownloading && <ArrowDownIcon isErrorDownloading={!!isErrorDownloading} />}
                </Container>
              )}

              {isPrivate ? (
                <QuestionsList
                  countries={countries}
                  detailEntCode={detailEntCode}
                  detailOrgCode={detailOrgCode}
                  detailUsrCode={detailUsrCode}
                  entCode={entCode}
                  errorCountries={errorCountries}
                  formType="private"
                  isClose={isClose}
                  isWillBePublic={isWillBePublic}
                  loadingCountries={loadingCountries}
                  onAddFile={onAddFile}
                  onDownloadFile={onDownloadFile}
                  onRemoveFile={onRemoveFile}
                  onResponse={onResponse}
                  onSignedUniquePass={onSignedUniquePass}
                  orgCode={orgCode}
                  publicUserDni={publicUserDni}
                  publicUserName={publicUserName}
                  questions={questions}
                  usrCode={usrCode}
                />
              ) : (
                <Tabs onChange={changeTabs} padding="24px 0" value="public_national" variant="standard">
                  <Tab id="public_national" label="Chileno">
                    {!publicUserDni && (
                      <Card color="blank" enableBorder margin="0 0 32px" padding="16px">
                        <Container
                          alignItems="center"
                          flexDirection="column"
                          justifyContent="center"
                          style={{
                            height: '150px'
                          }}
                        >
                          <Typography variant="body1">Para realizar su respuesta, ingrese con su ClaveÚnica</Typography>

                          <Button color="primary" disabled={isLoading} margin="16px 0" onClick={onSignedUniquePass}>
                            <UniquePassIcon alt="CU" src="/assets/icons/unique-pass.svg" />
                            ClaveÚnica
                          </Button>

                          <Typography color="black3" variant="body2">
                            Si aún no tienes tu clave, revisa cómo{' '}
                            <UniquePassLink>obtener tu Clave Única.</UniquePassLink>
                          </Typography>
                        </Container>
                      </Card>
                    )}

                    <QuestionsList
                      countries={countries}
                      detailEntCode={detailEntCode}
                      detailOrgCode={detailOrgCode}
                      detailUsrCode={detailUsrCode}
                      entCode={entCode}
                      errorCountries={errorCountries}
                      formType="public_national"
                      isClose={isClose}
                      isWillBePublic={isWillBePublic}
                      loadingCountries={loadingCountries}
                      onAddFile={onAddFile}
                      onDownloadFile={onDownloadFile}
                      onRemoveFile={onRemoveFile}
                      onResponse={onResponse}
                      onSignedUniquePass={onSignedUniquePass}
                      orgCode={orgCode}
                      publicUserDni={publicUserDni}
                      publicUserName={publicUserName}
                      questions={questions}
                      usrCode={usrCode}
                    />
                  </Tab>

                  <Tab id="public_foreign" label="Extranjero">
                    <QuestionsList
                      countries={countries}
                      detailEntCode={detailEntCode}
                      detailOrgCode={detailOrgCode}
                      detailUsrCode={detailUsrCode}
                      entCode={entCode}
                      errorCountries={errorCountries}
                      formType="public_foreign"
                      isClose={isClose}
                      isWillBePublic={isWillBePublic}
                      loadingCountries={loadingCountries}
                      onAddFile={onAddFile}
                      onDownloadFile={onDownloadFile}
                      onRemoveFile={onRemoveFile}
                      onResponse={onResponse}
                      onSignedUniquePass={onSignedUniquePass}
                      orgCode={orgCode}
                      publicUserDni={publicUserDni}
                      publicUserName={publicUserName}
                      questions={questions}
                      usrCode={usrCode}
                    />
                  </Tab>
                </Tabs>
              )}
            </>
          )}

          {questions.length === 0 && (
            <Toast border severity="warning" textDirection="vertical">
              Sin preguntas ni respuestas.
            </Toast>
          )}
        </>
      )}

      {!isLoading && isError && (
        <Toast
          border
          severity="error"
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor."
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}
    </Item>
  )
}

QuestionsAndAnswers.defaultProps = {
  countries: [],
  errorCountries: null,
  isClose: false,
  isDownloading: false,
  isError: false,
  isErrorDownloading: false,
  isLoading: false,
  isWillBePublic: false,
  loadingCountries: false,
  questions: []
}

QuestionsAndAnswers.propTypes = {
  countries: PropTypes.array,
  detailEntCode: PropTypes.shape().isRequired,
  detailOrgCode: PropTypes.shape().isRequired,
  detailUsrCode: PropTypes.shape().isRequired,
  errorCountries: PropTypes.any,
  isClose: PropTypes.bool,
  isDownloading: PropTypes.bool,
  isError: PropTypes.bool,
  isErrorDownloading: PropTypes.bool,
  isLoading: PropTypes.bool,
  isWillBePublic: PropTypes.bool,
  loadingCountries: PropTypes.bool,
  onAddFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onDownloadQuestionsAndAnswers: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onResponse: PropTypes.func.isRequired,
  onSignedUniquePass: PropTypes.func.isRequired,
  publicUserDni: PropTypes.string.isRequired,
  publicUserName: PropTypes.string.isRequired,
  questions: PropTypes.array
}

export default memo(QuestionsAndAnswers)
